import React from 'react';
import { Link, Redirect, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
    setFlowSource,
    setFromReviewFlag,
    setHideContactInfoFlag,
    setModifyFlag,
    setShowCancelButtonsFlag,
} from '../../Redux/ActionCreator';
import { formatDateLong, isAppointmentInPast } from '../../shared/Utils/TimeLocalization';
import {
    editAppointmentAxios,
    getCalendarInvite,
    setModifyEmpty,
    submitAppointment,
} from '../../shared/BackendCalls/SharedBackendGets';
import appointmentObject from '../../shared/Utils/POST/AppointmentObject';
import AnalyticsService, { setPageNameForAnalytics } from '../../shared/Utils/analytics.service';
import store from '../../Redux/Store';
import { shouldNotifyNativeApp } from '../../shared/Utils/nativeAppConfirmation';

const constants = require('../../shared/Constants/SharedConstants');
const flowConstants = require('../../shared/RoutingFlow/FlowComponentsConstants');

class AppointmentDateAndTime extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            nativeSubmitPending: false,
            redirectToConfirmation: false,
        };
    }

    setModifyTrue = () => {
        this.props.setModifyFlag('true');
    };

    setShowCancelButtonsTrue = () => {
        AnalyticsService().trackAppointmentSubmissionPageAndEvent('cancel');
        setPageNameForAnalytics('analyticsPageName', 'cancelAppointment');
        this.props.setShowCancelButtonsFlag(true);
    };

    setHideContactInfo = () => {
        this.props.setHideContactInfoFlag('true');
    };

    setFromReview = () => {
        this.props.setFromReviewFlag('true');
    };

    editAppointment = () => {
        setPageNameForAnalytics('analyticsPageName', 'edit');
        this.setModifyTrue();
    };

    ics = () => {
        const apptObject = appointmentObject();

        getCalendarInvite(this.props.vCountry, apptObject.bac, this.props.record);
    };

    isMyBrandOrigin() {
        const origin = this.props.ultimateOrigin;
        let isMyBrand;
        const MY_BRAND = 'OCAPP';

        if (origin && origin.indexOf(MY_BRAND) > -1) {
            isMyBrand = true;
        } else {
            isMyBrand = false;
        }
        return isMyBrand;
    }

    isNativeBound() {
        return !!window.ReactNativeWebView && shouldNotifyNativeApp(store.getState());
    }

    handleNativeSubmit = async (e) => {
        e.preventDefault();
        this.setState({ nativeSubmitPending: true });
        const result = await submitAppointment({ skipSpinner: true });
        if (!result?.nativeSent) {
            this.setState({ nativeSubmitPending: false, redirectToConfirmation: true });
        }
    };

    handleNativeEditSubmit = async (e) => {
        e.preventDefault();
        this.setState({ nativeSubmitPending: true });
        const result = await setModifyEmpty({ skipSpinner: true });
        if (!result?.nativeSent) {
            this.setState({ nativeSubmitPending: false, redirectToConfirmation: true });
        }
    };

    // Detect if running in native mobile app WebView (not mobile browser)
    isMobileNativeApp() {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;

        // iOS WebView detection - WKWebView has webkit.messageHandlers
        if (window.webkit && window.webkit.messageHandlers) {
            return true;
        }

        // Android WebView detection - user agent contains "wv"
        if (/Android/.test(userAgent) && /wv/.test(userAgent)) {
            return true;
        }

        return false;
    }

    consent = () => {
        const showPrivacyDetails = this.props.countryOptions.SHOW_PRIVACY_DETAILS;
        const showPrivacyDetailsModal = this.props.countryOptions.SHOW_PRIVACY_DETAILS_MODAL;
        if (showPrivacyDetails === 'true' && showPrivacyDetailsModal === 'true') {
            if (this.props.vDeploymentMode === 'REQUEST_FORM') {
                return (
                    <div className="view-privacy-policy">
                        <span
                            className="contact-disclaimer-label"
                            dangerouslySetInnerHTML={{
                                __html: `${this.props.vTranslations.CONSENT_PART_ONE_SUBIMIT_REQUEST} ${this.props.vTranslations.CONSENT_LINK} ${this.props.vTranslations.CONSENT_PART_TWO}`,
                            }}
                        />
                    </div>
                );
            }
            return (
                <div className="view-privacy-policy">
                    <span
                        className="contact-disclaimer-label"
                        dangerouslySetInnerHTML={{
                            __html: `${this.props.vTranslations.CONSENT_PART_ONE} ${this.props.vTranslations.CONSENT_LINK} ${this.props.vTranslations.CONSENT_PART_TWO}`,
                        }}
                    />
                </div>
            );
        }
        if (showPrivacyDetails === 'true' && showPrivacyDetailsModal === 'false') {
            if (this.props.vDeploymentMode === 'REQUEST_FORM') {
                return (
                    <span
                        className="contact-disclaimer-label"
                        dangerouslySetInnerHTML={{
                            __html:
                                this.props.vTranslations.CONTACT_CONSENT === undefined
                                    ? `${this.props.vTranslations.CONSENT_PART_ONE_SUBIMIT_REQUEST} ${this.props.vTranslations.CONSENT_LINK} ${this.props.vTranslations.CONSENT_PART_TWO}`
                                    : this.props.vTranslations.CONTACT_CONSENT,
                        }}
                    />
                );
            }
            return (
                <span
                    className="contact-disclaimer-label"
                    dangerouslySetInnerHTML={{
                        __html:
                            this.props.vTranslations.CONTACT_CONSENT === undefined
                                ? `${this.props.vTranslations.CONSENT_PART_ONE} ${this.props.vTranslations.CONSENT_LINK} ${this.props.vTranslations.CONSENT_PART_TWO}`
                                : this.props.vTranslations.CONTACT_CONSENT,
                    }}
                />
            );
        }
        return null;
    };

    render() {
        let showCalendarButton = false;
        let submitAppointmentButton;
        // Treat the appointment as past when the backend flag says so, or when its
        // scheduled date and time have already elapsed in the dealership timezone.
        // This prevents editing/cancelling passed appointments, which causes RTAS errors.
        const isPastAppointment =
            this.props.pastAppointment ||
            isAppointmentInPast(
                this.props.appointmentDate,
                this.props.appointmentStartTime,
                this.props.dealershipTimeZone
            );
        if (
            this.isMyBrandOrigin() ||
            this.props.countryOptions.ADD_TO_CALENDAR_MOBILE_APP !== 'false' ||
            this.props.dealershipSummary.displayCalendarButton
        ) {
            showCalendarButton = true;
        }

        if (this.props.modifyFlag === true) {
            const nativeBound = this.isNativeBound();
            submitAppointmentButton = (
                <Link
                    id="btnSubmit"
                    to={{ pathname: constants.CONFIRMATION_LINK }}
                    onClick={(e) => {
                        if (nativeBound) {
                            this.handleNativeEditSubmit(e);
                        } else {
                            setModifyEmpty();
                        }
                    }}
                    className="main-button button primary-button"
                    data-dtm="submit appointment top"
                >
                    {this.props.footerButtonText}
                </Link>
            );
        } else {
            const nativeBound = this.isNativeBound();
            submitAppointmentButton = (
                <Link
                    id="btnSubmit"
                    to={this.props.footerButtonLink}
                    onClick={(e) => {
                        if (nativeBound) {
                            this.handleNativeSubmit(e);
                        } else {
                            submitAppointment();
                        }
                    }}
                    data-dtm="submit appointment top"
                    className="main-button button primary-button"
                >
                    {this.props.footerButtonText}
                </Link>
            );
        }

        return (
            <>
                {this.state.redirectToConfirmation && <Redirect to={constants.CONFIRMATION_LINK} />}
                {this.state.nativeSubmitPending && (
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            background: '#ffffff',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 9999,
                        }}
                    >
                        <CircularProgress />
                        <p style={{ marginTop: '24px', fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
                            {this.props.vTranslations.IN_PROGRESS_VALIDATOR}
                        </p>
                    </div>
                )}
                <div className="review-confirm-appointment-date-time-section paper">
                    {this.props.showSpinner ? <CircularProgress /> : null}
                    <div
                        className={`review-confirm-appointment-date-time bold${
                            this.props.showSpinner ? ' invisible' : ''
                        }`}
                    >
                        {formatDateLong(this.props.selectedDate)}
                    </div>
                    {this.props.headerNavigation === constants.REVIEW && this.props.country === 'US' && (
                        <div className="submit-review">
                            <div>{this.consent()}</div>
                            <div className="submit-appointment-button">{submitAppointmentButton}</div>
                        </div>
                    )}
                    {this.props.headerNavigation === constants.CONFIRMATION ? (
                        !this.props.deleteAppointmentFlag ? (
                            !isPastAppointment ? (
                                this.props.dealerSummary.deploymentMode === 'REQUEST_FORM' ? null : (
                                    <div className="review-confirm-appointment-edit-cancel-link-container">
                                        {showCalendarButton ? (
                                            <>
                                                {/* Hide button only if: native mobile app AND hideAddToCalendarMobileApp flag is true */}
                                                {!(
                                                    this.isMobileNativeApp() &&
                                                    this.props.hideAddToCalendarMobileApp === true
                                                ) && (
                                                    <button
                                                        id="btnAddToCal"
                                                        onClick={(e) => {
                                                            if (this.props.showSpinner) {
                                                                e.preventDefault();
                                                            }
                                                            this.ics();
                                                        }}
                                                        className={`stat-button-link confirmation-appointment-link link${
                                                            this.props.showSpinner ? ' invisible' : ''
                                                        }`}
                                                        data-dtm="appointment details"
                                                    >
                                                        {this.props.footerButtonText}
                                                    </button>
                                                )}
                                            </>
                                        ) : null}
                                        <Link
                                            id="btnEditAppt"
                                            data-dtm="view appointment"
                                            className={`stat-button-link review-confirm-appointment-edit-cancel-link${
                                                this.props.showSpinner ? ' invisible' : ''
                                            }`}
                                            onClick={() => {
                                                this.setHideContactInfo();
                                                this.editAppointment();
                                            }}
                                            to={{
                                                pathname: this.props.vTranslations.APPOINTMENT_LINK,
                                            }}
                                        >
                                            {this.props.vTranslations.EDIT_APPOINTMENT}
                                        </Link>
                                        <button
                                            data-dtm="view appointment"
                                            className={`stat-button-link confirmation-appointment-link link${
                                                this.props.showSpinner ? ' invisible' : ''
                                            }`}
                                            onClick={this.setShowCancelButtonsTrue}
                                        >
                                            {this.props.vTranslations.CANCEL_APPOINTMENT}
                                        </button>
                                    </div>
                                )
                            ) : null
                        ) : null
                    ) : null}
                    {this.props.deleteAppointmentFlag ? (
                        <div className="review-confirm-appointment-date-time-cancel">
                            <div className="icon alert-caution-red" /> {this.props.vTranslations.CANCEL_SUCCESS}
                        </div>
                    ) : null}
                    {isPastAppointment ? (
                        <div className="review-confirm-appointment-date-time-cancel">
                            {this.props.vTranslations.VIEW_PAST_APPT}
                        </div>
                    ) : null}
                </div>
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    dealerSummary: state.prefillReducer.dealerSummaryObject.dealershipSummary,
    dealershipSummary: state.prefillReducer.dealerSummaryObject,
    deleteAppointmentFlag: state.confirmationReducer.deleteAppointmentFlag,
    flowSource: state.prefillReducer.flowSource,
    headerNavigation: state.headReducer.headerNavigation,
    modifyFlag: state.appointmentReducer.modifyFlag,
    pastAppointment: state.prefillReducer.editAppointmentObject.pastAppointment,
    appointmentDate: state.prefillReducer.editAppointmentObject.appointmentDate,
    appointmentStartTime:
        state.prefillReducer.editAppointmentObject.appointmentTime &&
        state.prefillReducer.editAppointmentObject.appointmentTime.start,
    dealershipTimeZone: state.prefillReducer.editAppointmentObject.dealershipTimeZone,
    recordLocator: state.reviewReducer.recordLocator,
    selectedDate: state.appointmentReducer.selectedDate,
    showSpinner: state.ossAppGlobals.showSpinner,
    vTranslations: state.countryOptionsReducer.translations,
    ultimateOrigin: state.prefillReducer.ultimateOrigin,
    countryOptions: state.countryOptionsReducer.countryOptions,
    origin: state.prefillReducer.origin,
    view: state.prefillReducer.view,
    footerButtonText: state.headReducer.footerButtonText,
    record: state.reviewReducer.recordLocator
        ? state.reviewReducer.recordLocator
        : state.prefillReducer.editAppointmentRecordLocator,
    vCountry: state.prefillReducer.country,
    footerButtonLink: state.headReducer.footerButtonLink,
    country: state.prefillReducer.country,
    vDeploymentMode: state.prefillReducer.dealerSummaryObject.dealershipSummary.deploymentMode,
});

// Wrapper component to inject LaunchDarkly flags
const DateAndTimeWithFlags = (props) => {
    const flags = useFlags();
    const { hideAddToCalendarMobileApp } = flags;

    return <AppointmentDateAndTime {...props} hideAddToCalendarMobileApp={hideAddToCalendarMobileApp} />;
};

const ConnectedComponent = connect(mapStateToProps, {
    setFlowSource,
    setFromReviewFlag,
    setHideContactInfoFlag,
    setModifyFlag,
    setShowCancelButtonsFlag,
    setModifyEmpty,
    submitAppointment,
})(DateAndTimeWithFlags);

export default ConnectedComponent;
withRouter(ConnectedComponent);
